/* script */
//
//
//

// Utils
import { cloneDeep, isEmpty } from 'lodash';

// Mixins
import unlayerMixin from 'shared/ui/mixins/unlayer';

// Constants
import DEFAULT_CONFIG from './registration_unlayer_editor_config.json';
import { injectHeaderFooterIntoDesign } from './registrationBlocks';

const __vue_script__ = {
    name: 'RegistrationUnlayerEditor',

    mixins: [unlayerMixin],

    props: {
        event: {
            type: Object,
            required: true
        },
        saveSpy: {
            type: Number,
            required: true
        },
        value: {
            type: Object,
            required: true
        },
        mergeTags: {
            type: Object,
            default: () => ({})
        },
        currentPage: {
            type: String,
            default: 'index'
        },
        pageList: {
            type: Array,
            default: () => []
        },
        headerNavItems: {
            type: Array,
            default: () => []
        },
        headerToolValues: {
            type: Object,
            default: null
        },
        footerToolValues: {
            type: Object,
            default: null
        },
    },

    data() {
        return {
            containerId: `editor-id-${ Date.now() }`,
            editor: null,
            isInternalUpdate: false,
        };
    },

    computed: {
        isFirstPage() {
            if (this.pageList.length > 0) {
                return this.currentPage === this.pageList[0].value;
            }
            return false;
        },
    },

    watch: {
        saveSpy() {
            this.exportDesignAndSave();
        },
        mergeTags(value) {
            this.setMergeTags(value);
        },
        value(newValue) {
            if (this.isInternalUpdate) {
                this.isInternalUpdate = false;
            } else {
                this.setDesign(newValue);
            }
        },
        headerNavItems: {
            handler() {
                this.syncHeaderFooterContent();
            },
            deep: true,
        },
    },

    async mounted() {
        await this.loadUnlayerScript();
        await this.initializeEditor();

        this.setDesign(this.value);
        this.setMergeTags(this.mergeTags);

        window.addEventListener('message', this.onMessage);
    },

    beforeDestroy() {
        window.removeEventListener('message', this.onMessage);
    },

    methods: {
        getEditorAppearance(theme, config) {
            return {
                ...config.appearance,
                theme: `modern_${ theme }`
            };
        },

        getEditorCustomJS(config) {
            return [
                ...config.customJS ?? [],
                `${ globalThis.location.origin }${ globalThis.BSTG.extLibs }/unlayer-tools.js`,
            ];
        },

        getCustomTools(toolData, config) {
            return {
                ...config.tools,
                'custom#countdown': {
                    data: {
                        startTime: this.event.description.start_time,
                    },
                },
                'custom#header': {
                    data: {
                        ...toolData,
                        nav: { navItems: toolData.navItems },
                    },
                    properties: {
                        pageName: {
                            editor: {
                                data: { pageName: toolData.pageName },
                            },
                        },
                        _readonlyNotice: {
                            editor: {
                                data: { isFirstPage: toolData.isFirstPage },
                            },
                        },
                    },
                },
                'custom#footer': {
                    data: toolData,
                    properties: {
                        pageName: {
                            editor: {
                                data: { pageName: toolData.pageName },
                            },
                        },
                        _readonlyNotice: {
                            editor: {
                                data: { isFirstPage: toolData.isFirstPage },
                            },
                        },
                    },
                },
            };
        },

        async initializeEditor() {
            if (this.editor) {
                return;
            }

            const theme = this.$utils.uiTheme.getCurrentTheme(this.$cookies);
            const config = cloneDeep(DEFAULT_CONFIG);
            const toolData = this.getHeaderFooterToolData();

            this.editor = await new Promise((resolve) => {
                const editor = this.unlayer.createEditor({
                    id: this.containerId,
                    displayMode: 'web',
                    projectId: config.projectId,
                    ...config,
                    appearance: this.getEditorAppearance(theme, config),
                    customJS: this.getEditorCustomJS(config),
                    tools: this.getCustomTools(toolData, config),
                    mergeTags: this.mergeTags
                });

                editor.registerCallback('image', this.onImageUpload);

                editor.addEventListener('editor:ready', () => {
                    this.registerBlockAddedCallback();
                    resolve(editor);
                });
            });
            this.$emit('loaded', true);
        },

        registerBlockAddedCallback() {
            this.unlayer.registerCallback('block:added', (newBlock, done) => {
                if (newBlock.type === 'custom#header' || newBlock.type === 'custom#footer') {
                    const { pageName } = this.getHeaderFooterToolData();
                    if (pageName) {
                        newBlock.values.page = { ...newBlock.values.page, pageName };
                    }
                }
                done(newBlock);
            });
        },

        setDesign(value) {
            if (this.editor) {
                if (isEmpty(value) || !value.body?.rows?.length) {
                    this.editor.loadBlank();
                    this.autoPopulateHeaderFooter();
                } else {
                    this.editor.loadDesign(value);
                    this.syncHeaderFooterContent();
                }
            }
        },

        autoPopulateHeaderFooter() {
            const { pageName, navItems } = this.getHeaderFooterToolData();

            setTimeout(() => {
                if (this.editor) {
                    this.editor.exportHtml(({ design }) => {
                        if (this.editor) {
                            const withHF = injectHeaderFooterIntoDesign(design, pageName, navItems);
                            this.isInternalUpdate = true;
                            this.editor.loadDesign(withHF);
                        }
                    });
                }
            }, 500);
        },

        setMergeTags(value) {
            this.editor?.setMergeTags(value);
        },

        getHeaderFooterToolData() {
            const currentPage = this.pageList.find(p => p.value === this.currentPage);
            const pageName = currentPage ? currentPage.label : this.currentPage;

            return {
                pageName,
                pageList: this.pageList.map(p => p.label),
                navItems: this.headerNavItems,
                isFirstPage: this.isFirstPage,
            };
        },

        /**
         * Unified sync for all header/footer content in the current design.
         * - On non-first pages: applies canonical values from the first page (propagation).
         * - Always: syncs navItems and pageName to match the current page context.
         */
        syncHeaderFooterContent() {
            if (this.editor) {
                this.editor.exportHtml(({ design }) => {
                    if (this.editor) {
                        const { pageName, navItems } = this.getHeaderFooterToolData();
                        const updated = this.syncDesignBlocks(design, navItems, pageName);

                        if (updated) {
                            this.isInternalUpdate = true;
                            this.editor.loadDesign(design);
                        }
                    }
                });
            }
        },

        syncDesignBlocks(design, navItems, pageName) {
            let updated = false;

            for (const row of design.body.rows) {
                for (const column of row.columns) {
                    for (const content of column.contents) {
                        if (content.type === 'custom#header') {
                            updated = this.syncHeader(content, navItems, pageName) || updated;
                        }
                        if (content.type === 'custom#footer') {
                            updated = this.syncFooter(content, pageName) || updated;
                        }
                    }
                }
            }

            return updated;
        },

        syncHeader(content, navItems, pageName) {
            if (!this.isFirstPage && this.headerToolValues) {
                content.values = cloneDeep(this.headerToolValues);
            }
            content.values.nav = { ...content.values.nav, navItems };
            content.values.page = { ...content.values.page, pageName };
            return true;
        },

        syncFooter(content, pageName) {
            if (!this.isFirstPage && this.footerToolValues) {
                content.values = cloneDeep(this.footerToolValues);
            }
            content.values.page = { ...content.values.page, pageName };
            return true;
        },

        /**
         * Export the current design and save.
         * On the first page, also extracts header/footer canonical values
         * so they can be propagated to other pages.
         */
        exportDesignAndSave() {
            if (this.editor) {
                this.editor.exportHtml(({ design, chunks }) => {
                    if (this.isFirstPage) {
                        this.emitCanonicalValues(design);
                    }
                    this.$emit('save', { design, html: chunks });
                });
            }
        },

        /**
         * Extract header/footer values from a design and emit them to the parent
         * so other pages can use the same values (propagation from first page).
         */
        emitCanonicalValues(design) {
            const values = { headerValues: null, footerValues: null };

            for (const row of design.body.rows) {
                for (const column of row.columns) {
                    for (const content of column.contents) {
                        if (content.type === 'custom#header' && !values.headerValues) {
                            values.headerValues = cloneDeep(content.values);
                        }
                        if (content.type === 'custom#footer' && !values.footerValues) {
                            values.footerValues = cloneDeep(content.values);
                        }
                    }
                }
            }

            if (values.headerValues || values.footerValues) {
                this.$emit('header-footer-updated', values);
            }
        },

        onMessage(event) {
            if (event.data?.action === 'openHeaderFooterModal') {
                this.$emit('open-header-footer-modal', event.data.tab);
            }
        },
    },
};

/* template */
var __vue_render__ = function () {
  var _vm = this
  var _h = _vm.$createElement
  var _c = _vm._self._c || _h
  return _c("div", {
    staticClass: "registration-unlayer-editor",
    attrs: { id: _vm.containerId },
  })
}
var __vue_staticRenderFns__ = []
__vue_render__._withStripped = true

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "apps/shared/src/ui/components/editors/registration/RegistrationUnlayerEditor.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__